/**
 * Google Drive folder-link parsing for the image-asset source field.
 *
 * Operators copy whatever the Drive UI gives them — that is a share URL, not an
 * ID. The backend column (`tenant_settings.image_asset_folder_id`) stores the
 * normalised form, so this converts the human input to the stored value.
 *
 * Stored-value format (Drive-sync contract §7 — no new column, no migration):
 *
 *   <folderId>                 // normal case
 *   <folderId>:<resourceKey>   // older link-scoped folders carrying ?resourcekey=
 *
 * The `:` delimiter is safe because Drive IDs and resourceKeys are both
 * `[A-Za-z0-9_-]+` — `:` can appear in neither. The resourceKey MUST be
 * preserved: without it the sync service cannot list an older folder at all
 * (Drive returns 404 as if the folder were unshared).
 *
 * Deliberately strict about the *kind* of link: a file link, a Doc/Sheet link,
 * or a "shared with me" search URL all look plausible to a human but would
 * silently store a value the image pipeline can never read. Those get a named
 * reason rather than a generic "invalid".
 */

/** A cleared field is valid input — it unsets the folder. */
export type DriveFolderParseResult =
  | {
      ok: true;
      /** The value to persist in `image_asset_folder_id` (null clears it). */
      stored: string | null;
      /** The bare folder ID — what the UI displays. */
      folderId: string | null;
      /** The `?resourcekey=` of an older link-scoped folder, when present. */
      resourceKey: string | null;
    }
  | { ok: false; reason: string };

/**
 * Drive IDs are URL-safe base64-ish. Modern folder IDs are 33 chars starting
 * with "1"; legacy ones are 28. We accept 15+ so we do not reject a valid ID
 * from an era we have not seen, while still rejecting obvious noise.
 */
const BARE_ID = /^[A-Za-z0-9_-]{15,}$/;

/** The stored composite form — re-pasting a stored value must round-trip. */
const STORED_COMPOSITE = /^([A-Za-z0-9_-]{15,}):([A-Za-z0-9_-]+)$/;

/** `/drive/folders/<id>`, including `/drive/u/0/folders/<id>`. */
const FOLDERS_PATH = /\/folders\/([A-Za-z0-9_-]{15,})/;

/** Legacy `?id=<id>` form still emitted by "Get link" on some accounts. */
const ID_QUERY = /[?&]id=([A-Za-z0-9_-]{15,})/;

/** Older folders carry `?resourcekey=<key>` — required to list them. */
const RESOURCE_KEY_QUERY = /[?&]resourcekey=([A-Za-z0-9_-]+)/i;

/** `/file/d/<id>/` — a single file, not a folder. */
const FILE_PATH = /\/file\/d\/([A-Za-z0-9_-]{15,})/;

export const DRIVE_FOLDER_REASONS = {
  fileNotFolder:
    'That link points to a single file, not a folder. Open the folder in Drive and copy the link from there.',
  notDrive:
    'That looks like a Google Docs/Sheets/Slides link. Image assets need a Drive folder link.',
  unrecognised:
    'That does not look like a Drive folder link or folder ID. Paste the link from the folder’s Share dialog, or the folder ID itself.',
} as const;

/** Split a stored `image_asset_folder_id` value on the FIRST `:`. */
export function splitDriveFolderValue(stored: string): {
  folderId: string;
  resourceKey: string | null;
} {
  const separator = stored.indexOf(':');
  if (separator === -1) return { folderId: stored, resourceKey: null };
  const key = stored.slice(separator + 1);
  return { folderId: stored.slice(0, separator), resourceKey: key || null };
}

function accepted(folderId: string, resourceKey: string | null): DriveFolderParseResult {
  return {
    ok: true,
    stored: resourceKey ? `${folderId}:${resourceKey}` : folderId,
    folderId,
    resourceKey,
  };
}

/**
 * Normalise a pasted Drive share URL (or a bare folder ID) to the stored value.
 *
 * Accepts, in order of how often an operator produces them:
 *   https://drive.google.com/drive/folders/<id>?usp=sharing
 *   https://drive.google.com/drive/folders/<id>?resourcekey=<key>
 *   https://drive.google.com/drive/u/0/folders/<id>
 *   https://drive.google.com/open?id=<id>
 *   <id>
 *   <id>:<resourceKey>   (a stored value pasted back — round-trips unchanged)
 */
export function parseDriveFolderId(raw: string | null | undefined): DriveFolderParseResult {
  const input = (raw ?? '').trim();

  // Empty clears the field — an operator must be able to disconnect a folder.
  if (!input) return { ok: true, stored: null, folderId: null, resourceKey: null };

  // A bare ID is already the stored form.
  if (BARE_ID.test(input) && !input.includes('/') && !input.includes('.')) {
    return accepted(input, null);
  }

  // A stored composite pasted back (or surviving a save round-trip) must not
  // lose its resourceKey.
  const composite = input.match(STORED_COMPOSITE);
  if (composite && !input.includes('/') && !input.includes('.')) {
    return accepted(composite[1], composite[2]);
  }

  const looksLikeUrl = /^https?:\/\//i.test(input) || input.includes('google.com');

  if (looksLikeUrl) {
    // Reject the confusable link types by name before the generic attempt, so
    // the operator is told what is actually wrong.
    if (FILE_PATH.test(input)) {
      return { ok: false, reason: DRIVE_FOLDER_REASONS.fileNotFolder };
    }
    if (/docs\.google\.com/i.test(input)) {
      return { ok: false, reason: DRIVE_FOLDER_REASONS.notDrive };
    }

    // Preserve the resourceKey of older link-scoped folders — stripping it
    // makes the folder unlistable for the sync service.
    const resourceKey = input.match(RESOURCE_KEY_QUERY)?.[1] ?? null;

    const fromPath = input.match(FOLDERS_PATH);
    if (fromPath) return accepted(fromPath[1], resourceKey);

    const fromQuery = input.match(ID_QUERY);
    if (fromQuery) return accepted(fromQuery[1], resourceKey);
  }

  return { ok: false, reason: DRIVE_FOLDER_REASONS.unrecognised };
}

/**
 * The canonical link for a stored value — lets the UI show what is configured.
 * Accepts either a bare folder ID or the `<folderId>:<resourceKey>` composite;
 * the resourceKey is re-appended as `?resourcekey=` so "Open in Drive" works
 * for older link-scoped folders.
 */
export function driveFolderUrl(stored: string): string {
  const { folderId, resourceKey } = splitDriveFolderValue(stored);
  const base = `https://drive.google.com/drive/folders/${folderId}`;
  return resourceKey ? `${base}?resourcekey=${resourceKey}` : base;
}
